import { ReactNode } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { Loader2 } from "lucide-react";
import NotFound from "@/pages/NotFound";
import { useOrganization } from "@/contexts/OrganizationContext";

type BooleanPermissionKeys = 
  | "isOwner" | "isAdmin" | "isManager" | "isMember" | "isTeamLeader"
  | "canManageSettings" | "canManageUsers" | "canManageTeams" | "canManageOwnTeam"
  | "canCreatePipelines" | "canManageAutomations" | "canViewReports"
  | "canManageScripts" | "canViewAllOpportunities" | "canViewTeamOpportunities"
  | "canCreateOpportunities" | "canExecuteScripts" | "canInviteUsers"
  | "canDeleteRecords";

interface OrgPermissionGuardProps {
  permission: BooleanPermissionKeys;
  children: ReactNode;
  fallback?: ReactNode;
}

export function OrgPermissionGuard({ permission, children, fallback }: OrgPermissionGuardProps) {
  const { isLoading } = useOrganization();
  const permissions = usePermissions();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  const hasAccess = permissions[permission] as boolean;

  if (!hasAccess) {
    return fallback ? <>{fallback}</> : <NotFound />;
  }

  return <>{children}</>;
}
